import { TextInput, Icon } from '@mgmresorts/mgm-ui';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { navigate } from 'hookrouter';
import React, { useEffect, useState } from 'react';

import { Container } from '../../../../components/ui/Container';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { useContent } from '../../../../hooks/useContent';
import { useLoadingLabel } from '../../../../hooks/useLoadingLabel';
import { useEvent } from '../../../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../../../utils/analytics/usePageLoadEvent';
import { IDENTITY_WIDGET_PATHS, IDW_FLOW_PARAM } from '../../../../utils/constants';
import { getPersistedParameters } from '../../../../utils/persistParameterContext';
import { safePhoneNumber } from '../../../../utils/safePhoneNumber';
import { useUserAccount } from '../../../../utils/userAccountContext';

import {
  ContentWrapper,
  IconWrapper,
  Title,
  Description,
  PhoneInputWrapper,
  SubmitButton,
  DisclaimerText,
} from './styles';

const phoneUtil = PhoneNumberUtil.getInstance();

/**
 * Validates that a phone number belongs to the US (not Canada or other +1 NANP countries).
 * Uses Google's libphonenumber which has comprehensive metadata for area code → country mapping.
 */
const isUSPhoneNumber = (phoneDigits: string): boolean => {
  try {
    const fullNumber = `+1${phoneDigits}`;
    const parsed = phoneUtil.parse(fullNumber, 'US');
    const regionCode = phoneUtil.getRegionCodeForNumber(parsed);
    return regionCode === 'US';
  } catch {
    return false;
  }
};

export const EnterPhoneNumber: React.FC = () => {
  usePageLoadEvent('identity: 2fa setup', 'sms auth');
  const trackEvent = useEvent();
  const [countryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { getContentByPath } = useContent();
  const submitPhoneNumberCTA = useLoadingLabel(isLoading, 'mfa.sms.enterPhoneNumber.submit');
  const params = getPersistedParameters();
  const currentFlow = params?.[IDW_FLOW_PARAM];

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
    if (error) setError('');
  };

  const handlePhoneNumberBlur = () => {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    const phoneDigits = digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;

    // Check if phone number is empty
    if (!phoneDigits || phoneDigits.length === 0) {
      setError(getContentByPath('mfa.sms.enterPhoneNumber.fields.verificationCode.required'));
      return;
    }

    // Check if phone number is invalid (not 10 digits or not properly formatted)
    if (phoneDigits.length !== 10 || !phoneNumber.includes(' ')) {
      setError(getContentByPath('mfa.sms.enterPhoneNumber.fields.verificationCode.error'));
      return;
    }

    // Validate US-only phone number (reject Canadian and other +1 NANP numbers)
    if (!isUSPhoneNumber(phoneDigits)) {
      setError('Only US phone numbers are supported');
      return;
    }
  };

  const { setUserPhoneNumber } = useUserAccount();
  // Validate phone number to enable/disable submit button
  const isSubmitDisabled = React.useMemo(() => {
    if (isLoading) return true;

    const digitsOnly = phoneNumber.replace(/\D/g, '');
    const phoneDigits = digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;

    // Check if phone number is empty or doesn't have exactly 10 digits
    if (!phoneDigits || phoneDigits.length !== 10) {
      return true;
    }

    // Check if the phone number includes a space (properly formatted)
    if (!phoneNumber.includes(' ')) {
      return true;
    }

    return false;
  }, [phoneNumber, isLoading]);

  // Track validation errors
  useEffect(() => {
    if (error) {
      trackEvent(
        getContentByPath<string>('mfa.analytics.error.eventName'),
        '2fa setup phone number input error',
        getContentByPath<string>('mfa.analytics.error.trackSuffix'),
        {
          errorField: 'Phone Number',
          errorDisplayed: error,
        },
      );
    }
  }, [error, trackEvent, getContentByPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    await trackEvent(
      'identity – unified MFA CTAs',
      'Submit Phone Number - SMS authentication',
      'unifiedmfactas',
      {},
      true,
    );

    try {
      const digitsOnly = phoneNumber.replace(/\D/g, '');
      const phoneDigits = digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;

      if (!phoneDigits || phoneDigits.length === 0) {
        setError(getContentByPath('mfa.sms.enterPhoneNumber.fields.verificationCode.required'));
        return;
      }

      if (phoneDigits.length !== 10 || !phoneNumber.includes(' ')) {
        setError(getContentByPath('mfa.sms.enterPhoneNumber.fields.verificationCode.error'));
        return;
      }

      // Validate US-only phone number (reject Canadian and other +1 NANP numbers)
      if (!isUSPhoneNumber(phoneDigits)) {
        setError('Only US phone numbers are supported');
        return;
      }

      const formattedNumber = safePhoneNumber(`${countryCode}${phoneDigits}`);
      setUserPhoneNumber(formattedNumber);
      navigate(IDENTITY_WIDGET_PATHS.MfaEnrollSmsVerify, true, { [IDW_FLOW_PARAM]: currentFlow });
    } catch (err) {
      console.error('Error enrolling phone number:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid='route-/mfa/enroll/sms'>
      <PageLayout
        title={getContentByPath('mfa.sms.enterPhoneNumber.title')}
        description={getContentByPath('mfa.sms.enterPhoneNumber.description')}
      >
        <Container height='auto'>
          <ContentWrapper>
            <IconWrapper>
              <Icon name='mobile-code' variant='outlined' size='large' aria-hidden='true' />
            </IconWrapper>

            <Title variant='heading-medium-1' as='h1'>
              {getContentByPath('mfa.sms.enterPhoneNumber.title')}
            </Title>

            <Description variant='body-regular-m'>
              {getContentByPath('mfa.sms.enterPhoneNumber.smsDescription')}
            </Description>

            <form onSubmit={handleSubmit}>
              <PhoneInputWrapper>
                <TextInput
                  allowedCountries={['US']}
                  variant='phone-number'
                  id='phoneNumber'
                  label='Phone Number'
                  value={phoneNumber}
                  error={!!error}
                  hint={error || getContentByPath('mfa.sms.enterPhoneNumber.supportedPhoneTypes')}
                  onPhoneNumberChange={handlePhoneNumberChange}
                  onBlur={handlePhoneNumberBlur}
                  disabled={isLoading}
                  testId='sms-phone-number-input'
                  data-private='true'
                  autoComplete='tel-national'
                />
              </PhoneInputWrapper>

              <DisclaimerText variant='body-regular-s'>
                {getContentByPath('mfa.sms.enterPhoneNumber.smsDisclaimer')}
              </DisclaimerText>
              <SubmitButton
                type='submit'
                variant='primary'
                size='large'
                fullWidth
                disabled={isSubmitDisabled}
                loading={isLoading}
                label={submitPhoneNumberCTA}
              ></SubmitButton>
            </form>
          </ContentWrapper>
        </Container>
      </PageLayout>
    </div>
  );
};

export default EnterPhoneNumber;
